/**
 * SlashCommandList - Burbuja horizontal de opciones al escribir "/"
 * Mismo estilo que el BubbleMenu al seleccionar texto
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';
import type { SlashCommandItem } from './slashCommand';
import styles from './SlashCommandList.module.scss';

export interface SlashCommandListProps {
  items: SlashCommandItem[];
  selectedIndex: number;
  onSelect: (item: SlashCommandItem) => void;
  onItemHover?: (index: number) => void;
  clientRect: (() => DOMRect | null) | null;
  decorationNode: Element | null;
}

function getPosition(clientRect: (() => DOMRect | null) | null): { top: number; left: number } {
  const rect = clientRect?.();
  if (!rect) return { top: 0, left: 0 };
  return {
    top: rect.bottom + 4,
    left: rect.left,
  };
}

export function SlashCommandList({
  items,
  selectedIndex,
  onSelect,
  onItemHover,
  clientRect,
}: SlashCommandListProps) {
  const [position, setPosition] = React.useState(() => getPosition(clientRect));
  let lastGroup = -1;

  React.useLayoutEffect(() => {
    const rect = clientRect?.();
    if (rect) {
      setPosition({ top: rect.bottom + 4, left: rect.left });
    }
  }, [clientRect, items]);

  if (items.length === 0) return null;

  return createPortal(
    <div
      className={styles.slashBubble}
      style={{ top: position.top, left: position.left }}
      role="toolbar"
    >
      {items.map((item, i) => {
        const needSep = lastGroup >= 0 && item.group !== lastGroup;
        lastGroup = item.group;
        return (
          <React.Fragment key={item.id}>
            {needSep && <span className={styles.slashBubble__sep} />}
            <button
              type="button"
              data-no-press-scale=""
              className={cn(
                styles.slashBubble__btn,
                i === selectedIndex && styles['slashBubble__btn--selected']
              )}
              title={item.label}
              aria-label={item.label}
              data-index={i}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(item);
              }}
              onMouseEnter={() => onItemHover?.(i)}
            >
              {item.bubbleContent}
            </button>
          </React.Fragment>
        );
      })}
    </div>,
    document.body
  );
}
